/**
 * NOURION Konfigurator Logik (ES Modul, rein, ohne Zugriff auf die Seite).
 *
 * Masterplan Abschnitt 4.2, Fachlogik Spezifikation 03 (1.2 Mengen, 2.3 Empfehlung, 2.4 Felder und
 * Umschalter, 2.5 Summe, 4.1 Validierung, 5.2 bis 5.4 Warenkorb, 6.4 bis 6.6 Link, Nummer, Anfrage).
 *
 * Grundsätze:
 *   * Alle Beträge sind ganze Cent. Preise kommen ausschließlich aus dem Katalog (Shopify Varianten).
 *   * Importiert nur @theme/nrn-rechenkern. Keine Globalen, kein DOM, kein Netzwerk.
 *   * Funktionen werfen nie wegen Nutzereingaben. Sie werfen nur bei Programmierfehlern
 *     (zum Beispiel ungültige Regeln oder unbekannte Feld IDs).
 *   * Sichtbare Texte entstehen nur über text(texte, schluessel, params) aus der Textdatei.
 *     Meldungen in fehler und warnungen sind technisch (Konsole), nie Oberflächentext.
 *   * Laufzeitinvariante: Summe aus Menge mal Variantenpreis ist centgenau gleich dem Rechenkern.
 */
import { kalkuliere, parseMenge } from '@theme/nrn-rechenkern';

/* ------------------------------------------------------------------------------------------------
 * Typen
 * --------------------------------------------------------------------------------------------- */

/**
 * @typedef {'managedRmm'|'managedBackup'|'managedEndpointSecurity'|'managedEmailArchiving'|'managedFirewall'|'managedSecurityAudit'|'managedCloudToCloudBackup'} ProduktId
 * @typedef {'basis'|'workplace'|'enterprise'} PaketId
 * @typedef {string} FeldId  zum Beispiel "managedBackup.cloudGb"
 * @typedef {string} PositionKey  zum Beispiel "managedBackup.synaxonCloudWorkload"
 * @typedef {{ ap: number, server: number, m365: number, standorte: number, homeoffice: number, gb: number }} Antworten
 * @typedef {{ 'managedBackup.storage': 'synaxon'|'acronis', 'managedBackup.billingModel': 'workload'|'gigabyte', 'managedCloudToCloudBackup.model': 'perUser'|'perStorage' }} SchalterWerte
 *
 * @typedef {Object} Zustand
 * @property {1} version
 * @property {Antworten|null} einstieg
 * @property {PaketId|'individuell'|null} paket
 * @property {Record<ProduktId, boolean>} produkte
 * @property {SchalterWerte} schalter
 * @property {Record<FeldId, number>} felder  alle 39 Felder, Checkbox 0 oder 1
 *
 * @typedef {{ code: string, feld?: FeldId, params?: Record<string, string|number> }} Hinweis
 *   code ist der Textschlüssel ohne Präfix "hinweise."
 *
 * @typedef {Object} Einstellungen  JSON "einstellungen" (Masterplan 4.6.4)
 * @property {'auto'|'abo'|'einmal'|'angebot'} [abrechnung]
 * @property {string} [hinweisAbrechnung]
 * @property {string} [abrechnungKurz]
 * @property {'zeile'|'summe'} [ustModus]
 * @property {string} [kontaktPfad]
 * @property {string} [seitenUrl]
 * @property {boolean} [designModus]
 *
 * @typedef {Object} PositionStatus
 * @property {'ok'|'gesperrt'|'info'} status
 * @property {null|'fehlt'|'nichtVerfuegbar'|'preisNull'|'doppelt'|'planFehlt'} grund
 * @property {number|null} variantId
 * @property {number|null} preisCent
 * @property {number|null} sellingPlanId
 * @property {string|null} planName
 *
 * @typedef {Object} KatalogStatus
 * @property {'kauf'|'nurAngebot'} modus
 * @property {null|'waehrung'|'inklUst'} grund
 * @property {Record<PositionKey, PositionStatus>} positionen
 * @property {string[]} warnungen  technisch, nur console.warn
 *
 * @typedef {Object} Zeile
 * @property {PositionKey} key
 * @property {ProduktId} produkt
 * @property {string} label
 * @property {number} variantId
 * @property {number} quantity  Warenkorbmenge q (Spezifikation 03 Abschnitt 1.2)
 * @property {number|null} sellingPlanId
 * @property {string|null} planName
 * @property {number} preisCent
 * @property {number} zeileCent  quantity mal preisCent
 * @property {number} eingabe  Rohwert n des Felds
 * @property {string} regel
 * @property {string|null} grundlage  sichtbarer Text bei Rundung oder Ableitung, sonst null
 *
 * @typedef {Object} Berechnung
 * @property {number} nettoCent
 * @property {number} ustCent
 * @property {number} bruttoCent
 * @property {number} jahrCent
 * @property {number|null} jeApCent
 * @property {Record<ProduktId, number>} proProdukt
 * @property {Zeile[]} zeilen
 * @property {Array<{ key: PositionKey, text: string }>} infoZeilen
 * @property {Array<{ key: PositionKey, grund: string, eingabe: number }>} blockiert
 * @property {{ art: 'abo'|'erstmonat'|'gesperrt', planNamen: string[] }} abrechnung
 * @property {boolean} gueltig
 * @property {boolean} invarianteOk
 * @property {boolean} warenkorbErlaubt
 * @property {null|'leer'|'eingabe'|'gesperrt'|'abrechnung'|'nurAngebot'|'invariante'} sperrgrund
 *
 * @typedef {Object} Regeln  Ergebnis von ladeRegeln (Indizes über der Regeldatei)
 * @property {Object} roh
 * @property {string} stand
 * @property {number} ustSatz
 * @property {Map<ProduktId, Object>} produkte
 * @property {Map<string, ProduktId>} produktNachCode
 * @property {Map<FeldId, Object>} felder
 * @property {Map<string, FeldId>} feldNachCode
 * @property {Map<PositionKey, Object>} positionen
 * @property {Map<string, PositionKey>} positionNachSku
 * @property {Map<FeldId, PositionKey[]>} positionenNachFeld
 * @property {Object[]} einstieg
 * @property {Object[]} schalter
 *
 * @typedef {{ items?: Array<Object>, item_count?: number, total_price?: number }} AjaxCart
 */

/* ------------------------------------------------------------------------------------------------
 * Konstanten
 * --------------------------------------------------------------------------------------------- */

const PRODUKT_IDS = Object.freeze([
  'managedRmm', 'managedBackup', 'managedEndpointSecurity', 'managedEmailArchiving',
  'managedFirewall', 'managedSecurityAudit', 'managedCloudToCloudBackup',
]);
const PAKET_IDS = Object.freeze(['basis', 'workplace', 'enterprise']);
/** Hash Codes der Pakete (Masterplan E9, Grammatik 03 Abschnitt 6.4). */
export const PAKET_CODES = Object.freeze({ basis: 'b', workplace: 'w', enterprise: 'e', individuell: 'x' });
const PAKET_NACH_CODE = Object.freeze({ b: 'basis', w: 'workplace', e: 'enterprise', x: 'individuell' });
const S_SPEICHER = 'managedBackup.storage';
const S_MODELL = 'managedBackup.billingModel';
const S_C2C = 'managedCloudToCloudBackup.model';
const SCHALTER_IDS = Object.freeze([S_SPEICHER, S_MODELL, S_C2C]);
const EINSTIEG_IDS = Object.freeze(['ap', 'server', 'm365', 'standorte', 'homeoffice', 'gb']);
const REGELN_ARTEN = new Set([
  'per_unit', 'per_unit_min', 'flag', 'flag_follows', 'per_started_1000',
  'per_started_1000_after_100', 'per_started_10_or_per_gb',
]);
/** Positionen der Regeldatei tragen keine Preise, Preise kommen nur aus dem Katalog: jeder Schlüssel mit Price oder Preis im Namen ist ein Fehler. */
const PREISFELD = /price|preis/i;
const ABRECHNUNGSARTEN = new Set(['auto', 'abo', 'einmal', 'angebot']);
const MAX_HASH_LAENGE = 2000;
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const ZAHL_IM_LINK = /^\d{1,6}$/;

const GELADEN = new WeakSet();
const CACHE = new WeakMap();

/* ------------------------------------------------------------------------------------------------
 * Texte und Formate
 * --------------------------------------------------------------------------------------------- */

/**
 * Sichtbarer Text aus der Textdatei. Ersetzt Platzhalter {name} durch params[name].
 * Fehlt der Schlüssel, entsteht "⟦schluessel⟧" (die E2E Prüfung erkennt so fehlende Texte).
 * Fehlt ein Parameter, bleibt der Platzhalter stehen.
 * @param {Object} texte  ganze Textdatei { schema, texte } oder nur die flache Abbildung
 * @param {string} key
 * @param {Record<string, string|number>} [params]
 * @returns {string}
 */
export function text(texte, key, params) {
  let abbildung = null;
  if (texte && typeof texte === 'object') {
    abbildung = texte.texte && typeof texte.texte === 'object' ? texte.texte : texte;
  }
  const vorlage = abbildung && Object.hasOwn(abbildung, key) && typeof abbildung[key] === 'string'
    ? abbildung[key] : null;
  if (vorlage === null) return `⟦${key}⟧`;
  if (!params || typeof params !== 'object') return vorlage;
  return vorlage.replace(/\{([a-zA-Z]+)\}/g, (ganz, name) =>
    (Object.hasOwn(params, name) && params[name] !== null && params[name] !== undefined) ? String(params[name]) : ganz);
}

const GELD = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const ZAHL = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });

/**
 * Formatiert ganze Cent als Eurobetrag, immer mit zwei Nachkommastellen ("1.234,56 €").
 * @param {number} cent
 * @returns {string}
 */
export function formatCent(cent) {
  const c = Number.isFinite(cent) ? Math.round(cent) : 0;
  return GELD.format(c / 100);
}

/**
 * Formatiert eine ganze Zahl mit Tausenderpunkten ("1.500").
 * @param {number} n
 * @returns {string}
 */
export function formatZahl(n) {
  return ZAHL.format(Number.isFinite(n) ? n : 0);
}

/* ------------------------------------------------------------------------------------------------
 * Regeln laden
 * --------------------------------------------------------------------------------------------- */

function istObjekt(x) {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
}
function istText(x) {
  return typeof x === 'string' && x.length > 0;
}
function istGanzzahl(x) {
  return Number.isSafeInteger(x);
}

/**
 * Liest die Regeldatei (Objekt oder JSON Text), prüft Aufbau und Querbezüge und baut Indizes.
 * Die volle Schemaprüfung läuft in den Node Tests (T27); hier wird geprüft, was die Logik braucht.
 * @param {unknown} json
 * @returns {{ ok: true, regeln: Regeln } | { ok: false, fehler: string[] }}
 */
export function ladeRegeln(json) {
  const fehler = [];
  let roh = json;
  if (typeof roh === 'string') {
    try { roh = JSON.parse(roh); } catch { return { ok: false, fehler: ['Regeldatei ist kein gültiges JSON'] }; }
  }
  if (!istObjekt(roh)) return { ok: false, fehler: ['Regeldatei ist kein Objekt'] };
  if (roh.schema !== 1) fehler.push('schema ist nicht 1');
  if (typeof roh.stand !== 'string' || !/^\d{4}\D\d{2}\D\d{2}$/.test(roh.stand)) fehler.push('stand fehlt');
  if (typeof roh.ustSatz !== 'number' || !Number.isFinite(roh.ustSatz) || roh.ustSatz < 0) fehler.push('ustSatz fehlt');
  for (const liste of ['produkte', 'schalter', 'einstieg', 'felder', 'positionen']) {
    if (!Array.isArray(roh[liste])) fehler.push(`${liste} ist keine Liste`);
  }
  if (fehler.length) return { ok: false, fehler };

  // Produkte
  const produkte = new Map();
  const produktNachCode = new Map();
  for (const p of roh.produkte) {
    if (!istObjekt(p) || !PRODUKT_IDS.includes(p.id)) { fehler.push(`Produkt unbekannt: ${p?.id}`); continue; }
    if (produkte.has(p.id)) fehler.push(`Produkt doppelt: ${p.id}`);
    if (typeof p.code !== 'string' || !/^[a-z]$/.test(p.code) || produktNachCode.has(p.code)) fehler.push(`Produktcode ungültig: ${p.id}`);
    if (!istText(p.titel)) fehler.push(`Produkttitel fehlt: ${p.id}`);
    produkte.set(p.id, { ...p, index: produkte.size });
    produktNachCode.set(p.code, p.id);
  }
  for (const id of PRODUKT_IDS) if (!produkte.has(id)) fehler.push(`Produkt fehlt: ${id}`);

  // Schalter
  const schalterNachId = new Map();
  for (const s of roh.schalter) {
    if (!istObjekt(s) || !SCHALTER_IDS.includes(s.id)) { fehler.push(`Schalter unbekannt: ${s?.id}`); continue; }
    if (!Array.isArray(s.werte) || s.werte.length < 2) { fehler.push(`Schalter ohne Werte: ${s.id}`); continue; }
    const codes = new Set();
    for (const w of s.werte) {
      if (!istObjekt(w) || !istText(w.id) || typeof w.code !== 'string' || !/^[a-z]$/.test(w.code) || codes.has(w.code)) {
        fehler.push(`Schalterwert ungültig: ${s.id}`);
      } else codes.add(w.code);
    }
    if (!s.werte.some(w => w?.id === s.standard)) fehler.push(`Schalter Standard ungültig: ${s.id}`);
    schalterNachId.set(s.id, s);
  }
  for (const id of SCHALTER_IDS) if (!schalterNachId.has(id)) fehler.push(`Schalter fehlt: ${id}`);

  // Einstieg
  const einstiegNachId = new Map();
  for (const f of roh.einstieg) {
    if (!istObjekt(f) || !EINSTIEG_IDS.includes(f.id)) { fehler.push(`Einstiegsfrage unbekannt: ${f?.id}`); continue; }
    if (!istGanzzahl(f.min) || !istGanzzahl(f.max) || !istGanzzahl(f.standard) || f.min < 0 || f.min > f.standard || f.standard > f.max) {
      fehler.push(`Einstiegsgrenzen ungültig: ${f.id}`);
    }
    einstiegNachId.set(f.id, f);
  }
  for (const id of EINSTIEG_IDS) if (!einstiegNachId.has(id)) fehler.push(`Einstiegsfrage fehlt: ${id}`);

  // Felder
  const felder = new Map();
  const feldNachCode = new Map();
  for (const f of roh.felder) {
    if (!istObjekt(f) || !istText(f.id)) { fehler.push('Feld ohne id'); continue; }
    if (felder.has(f.id)) fehler.push(`Feld doppelt: ${f.id}`);
    if (!produkte.has(f.product) || !f.id.startsWith(`${f.product}.`)) fehler.push(`Feld ohne gültiges Produkt: ${f.id}`);
    if (typeof f.code !== 'string' || !/^[a-z][a-z0-9]{1,3}$/.test(f.code) || feldNachCode.has(f.code)) fehler.push(`Feldcode ungültig: ${f.id}`);
    if (f.typ !== 'zahl' && f.typ !== 'checkbox') fehler.push(`Feldtyp ungültig: ${f.id}`);
    if (!istGanzzahl(f.max) || f.max < 1 || f.max > 999999 || (f.typ === 'checkbox' && f.max !== 1)) fehler.push(`Feld max ungültig: ${f.id}`);
    if (!istText(f.label)) fehler.push(`Feld ohne label: ${f.id}`);
    if (f.bezugImSatz !== undefined && !istText(f.bezugImSatz)) fehler.push(`Feld bezugImSatz ungültig: ${f.id}`);
    felder.set(f.id, f);
    feldNachCode.set(f.code, f.id);
  }
  for (const f of felder.values()) {
    for (const bezug of ['maxOf', 'follows']) {
      if (f[bezug] === null || f[bezug] === undefined) continue;
      const ziel = felder.get(f[bezug]);
      if (!ziel || ziel.product !== f.product) fehler.push(`Feld ${bezug} ungültig: ${f.id}`);
    }
    if (f.sichtbarWenn !== null && f.sichtbarWenn !== undefined) {
      const s = schalterNachId.get(f.sichtbarWenn?.schalter);
      if (!s || !s.werte.some(w => w?.id === f.sichtbarWenn.wert)) fehler.push(`Feld sichtbarWenn ungültig: ${f.id}`);
    }
  }

  // Positionen
  const positionen = new Map();
  const positionNachSku = new Map();
  const positionenNachFeld = new Map();
  for (const p of roh.positionen) {
    if (!istObjekt(p) || !istText(p.key)) { fehler.push('Position ohne key'); continue; }
    if (Object.keys(p).some(k => PREISFELD.test(k))) fehler.push(`Position mit Preisfeld: ${p.key}`);
    if (p.key !== `${p.product}.${p.id}`) fehler.push(`Positionsschlüssel ungültig: ${p.key}`);
    if (positionen.has(p.key)) fehler.push(`Position doppelt: ${p.key}`);
    if (!produkte.has(p.product)) fehler.push(`Position ohne gültiges Produkt: ${p.key}`);
    const feld = felder.get(p.feld);
    if (!feld || feld.product !== p.product) fehler.push(`Position ohne gültiges Feld: ${p.key}`);
    if (!REGELN_ARTEN.has(p.rule)) fehler.push(`Regel unbekannt: ${p.key}`);
    if (p.warenkorb !== 'zeile' && p.warenkorb !== 'info') fehler.push(`warenkorb ungültig: ${p.key}`);
    if (p.warenkorb === 'zeile') {
      if (!istText(p.sku)) fehler.push(`SKU fehlt: ${p.key}`);
      else if (positionNachSku.has(p.sku)) fehler.push(`SKU doppelt: ${p.sku}`);
      else positionNachSku.set(p.sku, p.key);
    }
    if (p.rule === 'per_unit_min' && (!istGanzzahl(p.minimumQuantity) || p.minimumQuantity < 1)) fehler.push(`Mindestabnahme fehlt: ${p.key}`);
    if (!istText(p.label)) fehler.push(`Position ohne label: ${p.key}`);
    positionen.set(p.key, p);
    if (feld) {
      if (!positionenNachFeld.has(p.feld)) positionenNachFeld.set(p.feld, []);
      positionenNachFeld.get(p.feld).push(p.key);
    }
  }
  for (const p of positionen.values()) {
    if (p.rule === 'flag_follows') {
      const ziel = positionen.get(p.follows);
      if (!ziel || ziel.product !== p.product) fehler.push(`follows ungültig: ${p.key}`);
    }
    if (p.maxOf !== null && p.maxOf !== undefined) {
      const ziel = positionen.get(p.maxOf);
      if (!ziel || ziel.product !== p.product) fehler.push(`maxOf ungültig: ${p.key}`);
    }
  }
  for (const id of felder.keys()) if (!positionenNachFeld.has(id)) fehler.push(`Feld ohne Position: ${id}`);

  if (fehler.length) return { ok: false, fehler };

  const regeln = {
    roh,
    stand: roh.stand,
    ustSatz: roh.ustSatz,
    produkte,
    produktNachCode,
    felder,
    feldNachCode,
    positionen,
    positionNachSku,
    positionenNachFeld,
    einstieg: roh.einstieg.slice(),
    schalter: roh.schalter.slice(),
    schalterNachId,
    einstiegNachId,
    produktReihenfolge: [...produkte.keys()],
  };
  GELADEN.add(regeln);
  return { ok: true, regeln };
}

/**
 * Liefert geladene Regeln. Akzeptiert das Ergebnis von ladeRegeln, dessen Feld regeln oder die rohe
 * Regeldatei (wird einmal geladen und zwischengespeichert). Ungültige Regeln sind ein Programmierfehler.
 * @param {unknown} regeln
 * @returns {Regeln}
 */
function idx(regeln) {
  if (regeln && GELADEN.has(regeln)) return regeln;
  if (istObjekt(regeln) && regeln.ok === true && regeln.regeln && GELADEN.has(regeln.regeln)) return regeln.regeln;
  if (istObjekt(regeln)) {
    const vorhanden = CACHE.get(regeln);
    if (vorhanden) return vorhanden;
    const r = ladeRegeln(regeln);
    if (r.ok) { CACHE.set(regeln, r.regeln); return r.regeln; }
    throw new Error(`Regeln ungültig: ${r.fehler.join('; ')}`);
  }
  if (typeof regeln === 'string') {
    const r = ladeRegeln(regeln);
    if (r.ok) return r.regeln;
    throw new Error(`Regeln ungültig: ${r.fehler.join('; ')}`);
  }
  throw new Error('Regeln fehlen');
}

/* ------------------------------------------------------------------------------------------------
 * Einstellungen und Katalog
 * --------------------------------------------------------------------------------------------- */

/**
 * Ergänzt fehlende oder ungültige Einstellungen um die Standardwerte (Masterplan 4.6.4, E3).
 * @param {Einstellungen|undefined} einstellungen
 * @returns {Einstellungen & { abrechnung: string, hinweisAbrechnung: string, abrechnungKurz: string, ustModus: string }}
 */
export function normalisiereEinstellungen(einstellungen) {
  const e = istObjekt(einstellungen) ? einstellungen : {};
  return {
    ...e,
    abrechnung: ABRECHNUNGSARTEN.has(e.abrechnung) ? e.abrechnung : 'auto',
    hinweisAbrechnung: typeof e.hinweisAbrechnung === 'string' ? e.hinweisAbrechnung.trim() : '',
    abrechnungKurz: typeof e.abrechnungKurz === 'string' ? e.abrechnungKurz.trim() : '',
    ustModus: e.ustModus === 'summe' ? 'summe' : 'zeile',
  };
}

/**
 * Prüft den Katalog aus Liquid gegen die Regeln (Spezifikation 03 Abschnitt 5.4 und 6.2).
 * Ergebnis je Position: ok, gesperrt (mit Grund) oder info (keine Warenkorbzeile).
 * Modus nurAngebot bei Währung ungleich EUR oder Preisen inklusive Steuer.
 * @param {Regeln|Object} regeln
 * @param {unknown} katalog
 * @param {Einstellungen} einstellungen
 * @returns {KatalogStatus}
 */
export function pruefeKatalog(regeln, katalog, einstellungen) {
  const R = idx(regeln);
  const e = normalisiereEinstellungen(einstellungen);
  const warnungen = [];
  let modus = 'kauf';
  let grund = null;

  const kat = istObjekt(katalog) ? katalog : null;
  if (!kat) warnungen.push('Katalog fehlt oder ist kein Objekt');
  const varianten = kat && Array.isArray(kat.variants) ? kat.variants : [];
  if (kat && !Array.isArray(kat.variants)) warnungen.push('Katalog ohne variants');

  if (kat) {
    if (kat.waehrung !== 'EUR') { modus = 'nurAngebot'; grund = 'waehrung'; warnungen.push(`Währung ${String(kat.waehrung)} statt EUR`); }
    else if (kat.preiseInklUst === true) { modus = 'nurAngebot'; grund = 'inklUst'; warnungen.push('Preise inklusive Steuer'); }
  }

  /** @type {Map<string, Object[]>} */
  const nachSku = new Map();
  for (const v of varianten) {
    if (!istObjekt(v) || !istText(v.sku)) { warnungen.push('Katalogeintrag ohne SKU'); continue; }
    if (!nachSku.has(v.sku)) nachSku.set(v.sku, []);
    nachSku.get(v.sku).push(v);
    if (!R.positionNachSku.has(v.sku)) warnungen.push(`SKU ohne Regel: ${v.sku}`);
  }

  const positionen = {};
  for (const pos of R.positionen.values()) {
    if (pos.warenkorb === 'info') {
      positionen[pos.key] = { status: 'info', grund: null, variantId: null, preisCent: null, sellingPlanId: null, planName: null };
      continue;
    }
    const gesperrt = g => ({ status: 'gesperrt', grund: g, variantId: null, preisCent: null, sellingPlanId: null, planName: null });
    const treffer = nachSku.get(pos.sku) ?? [];
    if (treffer.length > 1) {
      warnungen.push(`SKU doppelt im Katalog: ${pos.sku}`);
      positionen[pos.key] = gesperrt('doppelt');
      continue;
    }
    const v = treffer[0];
    if (!v || !istGanzzahl(v.variantId) || v.variantId < 1 || !istGanzzahl(v.preis) || v.preis < 0) {
      if (v) warnungen.push(`Katalogeintrag ungültig: ${pos.sku}`);
      positionen[pos.key] = gesperrt('fehlt');
      continue;
    }
    const handle = R.produkte.get(pos.product)?.handle;
    if (handle && v.produkt !== handle) warnungen.push(`Produkt passt nicht zur SKU ${pos.sku}: ${String(v.produkt)}`);
    if (pos.variantTitel && v.titel !== pos.variantTitel) warnungen.push(`Variantentitel weicht ab: ${pos.sku}`);

    const hatPlan = istGanzzahl(v.sellingPlanId) && v.sellingPlanId > 0;
    const nutztPlan = hatPlan && e.abrechnung !== 'einmal';
    const preisCent = nutztPlan && istGanzzahl(v.planPreis) && v.planPreis >= 0 ? v.planPreis : v.preis;

    let g = null;
    if (v.verfuegbar !== true) g = 'nichtVerfuegbar';
    else if (preisCent === 0) { g = 'preisNull'; warnungen.push(`Preis 0 bei abrechenbarer Position: ${pos.sku}`); }
    else if (e.abrechnung === 'abo' && !hatPlan) g = 'planFehlt';
    else if (v.abonnementPflicht === true && !nutztPlan) g = 'planFehlt';

    positionen[pos.key] = g ? gesperrt(g) : {
      status: 'ok', grund: null, variantId: v.variantId, preisCent,
      sellingPlanId: nutztPlan ? v.sellingPlanId : null,
      planName: nutztPlan && typeof v.planName === 'string' ? v.planName : null,
    };
  }
  return { modus, grund, positionen, warnungen };
}

/* ------------------------------------------------------------------------------------------------
 * Zustand
 * --------------------------------------------------------------------------------------------- */

/**
 * Ausgangszustand: alle Mengen 0, alle Produkte aus, Schalter Standard, ohne Einstieg und Paket.
 * @param {Regeln|Object} regeln
 * @returns {Zustand}
 */
export function standardZustand(regeln) {
  const R = idx(regeln);
  const produkte = {};
  for (const id of R.produktReihenfolge) produkte[id] = false;
  const schalter = {};
  for (const id of SCHALTER_IDS) schalter[id] = R.schalterNachId.get(id).standard;
  const felder = {};
  for (const id of R.felder.keys()) felder[id] = 0;
  return { version: 1, einstieg: null, paket: null, produkte, schalter, felder };
}

/** Prüft Einstiegsantworten gegen die Grenzen der Regeldatei. */
function einstiegGueltig(einstieg, R) {
  if (!istObjekt(einstieg)) return false;
  for (const id of EINSTIEG_IDS) {
    const f = R.einstiegNachId.get(id);
    const v = einstieg[id];
    if (!istGanzzahl(v) || v < f.min || v > f.max) return false;
  }
  return true;
}

/**
 * Bringt einen beliebigen Zustand in eine gültige, vollständige Form: unbekannte Werte fallen weg,
 * ungültige Mengen werden 0, Mengen über max werden max, Checkbox 0 oder 1, Advanced höchstens Basis.
 * Werte inaktiver Produkte und des inaktiven C2C Modells bleiben erhalten (sie zählen nur nicht).
 * @param {unknown} zustand
 * @param {Regeln|Object} regeln
 * @returns {Zustand}
 */
export function normalisiereZustand(zustand, regeln) {
  const R = idx(regeln);
  const z = istObjekt(zustand) ? zustand : {};
  const neu = standardZustand(R);
  if (einstiegGueltig(z.einstieg, R)) {
    neu.einstieg = Object.fromEntries(EINSTIEG_IDS.map(id => [id, z.einstieg[id]]));
  }
  if (PAKET_IDS.includes(z.paket) || z.paket === 'individuell') neu.paket = z.paket;
  if (istObjekt(z.produkte)) for (const id of R.produktReihenfolge) neu.produkte[id] = z.produkte[id] === true;
  if (istObjekt(z.schalter)) {
    for (const id of SCHALTER_IDS) {
      if (R.schalterNachId.get(id).werte.some(w => w.id === z.schalter[id])) neu.schalter[id] = z.schalter[id];
    }
  }
  if (istObjekt(z.felder)) {
    for (const [id, f] of R.felder) {
      const v = z.felder[id];
      neu.felder[id] = istGanzzahl(v) && v > 0 ? Math.min(v, f.max) : 0;
    }
  }
  return passeAbhaengigkeitenAn(neu, R).zustand;
}

/** Liste der Felder, deren Wert im Zustand ungültig ist (keine ganze Zahl, außerhalb der Grenzen, über der Basis). */
function ungueltigeFelder(zustand, R) {
  const fehlerhaft = [];
  const felder = istObjekt(zustand?.felder) ? zustand.felder : {};
  for (const [id, f] of R.felder) {
    const v = felder[id] ?? 0;
    if (!istGanzzahl(v) || v < 0 || v > f.max) { fehlerhaft.push(id); continue; }
    if (f.maxOf) {
      const basis = felder[f.maxOf] ?? 0;
      if (istGanzzahl(basis) && v > basis) fehlerhaft.push(id);
    }
  }
  return fehlerhaft;
}

/** Feld sichtbar unter den aktuellen Schaltern (C2C Modell exklusiv). */
function sichtbar(feld, zustand) {
  return !feld.sichtbarWenn || zustand.schalter[feld.sichtbarWenn.schalter] === feld.sichtbarWenn.wert;
}

/**
 * Ist das Feld unter den aktuellen Schaltern sichtbar? Nur das gewählte C2C Modell ist sichtbar.
 * @param {FeldId} feldId
 * @param {Zustand} zustand
 * @param {Regeln|Object} regeln
 * @returns {boolean}
 */
export function feldSichtbar(feldId, zustand, regeln) {
  const f = idx(regeln).felder.get(feldId);
  if (!f) throw new Error(`Feld unbekannt: ${feldId}`);
  return sichtbar(f, zustand);
}

/**
 * Wirkt die Position unter Produktauswahl und Schaltern? Gleiche Logik wie kalkuliere im Rechenkern
 * (Backup Speicherort und Modell, C2C Modell exklusiv).
 * @param {Object|PositionKey} position  Position der Regeldatei oder ihr Schlüssel
 * @param {Zustand} zustand
 * @param {Regeln|Object} [regeln]  nötig, wenn position ein Schlüssel ist
 * @returns {boolean}
 */
export function positionAktiv(position, zustand, regeln) {
  const pos = typeof position === 'string' ? idx(regeln).positionen.get(position) : position;
  if (!pos) throw new Error(`Position unbekannt: ${String(position)}`);
  return aktiv(pos, zustand);
}

function aktiv(pos, z) {
  if (!z.produkte[pos.product]) return false;
  if (pos.product === 'managedBackup') {
    const modell = z.schalter[S_MODELL];
    if (pos.billingModel === 'workload' && modell !== 'workload') return false;
    if (pos.billingModel === 'gigabyte' && modell !== 'gigabyte') return false;
    if (pos.storage && pos.storage !== z.schalter[S_SPEICHER]) return false;
  }
  if (pos.product === 'managedCloudToCloudBackup' && pos.c2cModel !== z.schalter[S_C2C]) return false;
  return true;
}

/* ------------------------------------------------------------------------------------------------
 * Empfehlung (Spezifikation 03 Abschnitt 2.3)
 * --------------------------------------------------------------------------------------------- */

/** Einstiegsantwort als ganze Zahl zwischen min und max (max ohne Angabe unbegrenzt). */
function antwortWert(antworten, id, min, max = Number.POSITIVE_INFINITY) {
  const v = Number(antworten?.[id]);
  const n = Number.isFinite(v) ? Math.floor(v) : 0;
  return Math.min(max, Math.max(min, n));
}

/**
 * Markierung "Passend für Sie": Enterprise bei mehreren Standorten oder Homeoffice, sonst Workplace,
 * wenn Server, Microsoft 365 Nutzer oder Daten vorhanden sind, sonst Basis.
 * @param {Antworten} antworten
 * @returns {PaketId}
 */
export function empfohlenesPaket(antworten) {
  const st = antwortWert(antworten, 'standorte', 0);
  const h = antwortWert(antworten, 'homeoffice', 0);
  if (st > 1 || h > 0) return 'enterprise';
  const s = antwortWert(antworten, 'server', 0);
  const m = antwortWert(antworten, 'm365', 0);
  const d = antwortWert(antworten, 'gb', 0);
  if (s > 0 || m > 0 || d > 0) return 'workplace';
  return 'basis';
}

/**
 * Firewall Größe je Standort (Annahme F12): bis 10 Basic, bis 30 Standard 30, sonst Standard 75.
 * @param {number} apJeStandort
 * @returns {{ feld: FeldId, gross: boolean }}
 */
function firewallFuer(apJeStandort) {
  if (apJeStandort <= 10) return { feld: 'managedFirewall.basicFirewall', gross: false };
  if (apJeStandort <= 30) return { feld: 'managedFirewall.standardFirewall30', gross: false };
  return { feld: 'managedFirewall.standardFirewall75', gross: apJeStandort > 75 };
}

/**
 * Zustand für ein Paket aus den Einstiegsantworten (Mengenformeln 03 Abschnitt 2.3).
 * Schalter immer SYNAXON Storage, Workload, Pro Benutzer.
 * @param {Antworten} antworten
 * @param {PaketId} paket
 * @param {Regeln|Object} regeln
 * @returns {{ zustand: Zustand, hinweise: Hinweis[] }}
 */
export function empfehle(antworten, paket, regeln) {
  const R = idx(regeln);
  if (!PAKET_IDS.includes(paket)) throw new Error(`Paket unbekannt: ${String(paket)}`);
  // Antworten über der Obergrenze der Frage gelten als Obergrenze, sonst fiele e= aus dem Link (einstiegGueltig)
  // und jeApCent teilte durch eine Zahl, die die Oberfläche nie zulässt (Kandidat 84).
  const lies = (id, min) => antwortWert(antworten, id, min, R.einstiegNachId.get(id).max);
  const a = lies('ap', 1);
  const s = lies('server', 0);
  const m = lies('m365', 0);
  const st = lies('standorte', 1);
  const h = lies('homeoffice', 0);
  const d = lies('gb', 0);
  const g = a + s;
  const p = m > 0 ? m : a;
  const hinweise = [];

  const z = standardZustand(R);
  z.einstieg = { ap: a, server: s, m365: m, standorte: st, homeoffice: h, gb: d };
  z.paket = paket;
  const setze = (id, wert) => { z.felder[id] = wert; };
  const an = (...ids) => { for (const id of ids) z.produkte[id] = true; };

  setze('managedRmm.workstations', a);
  setze('managedRmm.servers', s);
  an('managedRmm', 'managedEndpointSecurity');

  if (paket === 'basis') {
    setze('managedEndpointSecurity.entry', g);
  } else {
    an('managedBackup', 'managedEmailArchiving');
    setze('managedBackup.workstations', a);
    setze('managedBackup.servers', s);
    setze('managedBackup.cloudGb', d);
    setze('managedEmailArchiving.archives', p);
    setze('managedEmailArchiving.journaling', p > 0 ? 1 : 0);
    if (paket === 'workplace') {
      setze('managedEndpointSecurity.advanced', g);
      setze('managedBackup.m365', m);
    } else {
      an('managedFirewall', 'managedSecurityAudit', 'managedCloudToCloudBackup');
      setze('managedRmm.customerAccess', 1);
      setze('managedEndpointSecurity.complete', g);
      const fw = firewallFuer(Math.ceil(a / st));
      setze(fw.feld, st);
      if (fw.gross) hinweise.push({ code: 'firewallGross' });
      setze('managedFirewall.homeOfficeConnector', h);
      setze('managedSecurityAudit.internalScan', g);
      setze('managedSecurityAudit.externalScan', st);
      setze('managedSecurityAudit.assetDiscovery', 1);
      setze('managedCloudToCloudBackup.m365', m);
    }
  }
  // Grenzen der Felder einhalten (Einstiegsgrenzen liegen ohnehin darunter)
  for (const [id, f] of R.felder) if (z.felder[id] > f.max) z.felder[id] = f.max;
  const angepasst = passeAbhaengigkeitenAn(z, R);
  return { zustand: angepasst.zustand, hinweise: [...hinweise, ...angepasst.hinweise] };
}

/**
 * Name eines Basisfelds mitten im Satz ("höchstens so viele wie virtuelle Maschinen"): bezugImSatz der
 * Regeldatei, sonst das Feldlabel (Befund 21).
 * @param {Object} feld
 * @returns {string}
 */
const bezugText = (feld) => feld.bezugImSatz ?? feld.label;

/**
 * Erzwingt Advanced höchstens Basis (Feld maxOf) und Checkbox 0 oder 1. Liefert einen neuen Zustand
 * und je Anpassung einen Hinweis "angepasst" (Spezifikation 03 Abschnitt 2.4).
 * @param {Zustand} zustand
 * @param {Regeln|Object} regeln
 * @returns {{ zustand: Zustand, hinweise: Hinweis[] }}
 */
export function passeAbhaengigkeitenAn(zustand, regeln) {
  const R = idx(regeln);
  const z = { ...zustand, produkte: { ...zustand.produkte }, schalter: { ...zustand.schalter }, felder: { ...zustand.felder } };
  if (zustand.einstieg) z.einstieg = { ...zustand.einstieg };
  const hinweise = [];
  for (const [id, f] of R.felder) {
    let v = z.felder[id];
    if (!istGanzzahl(v) || v < 0) v = 0;
    if (f.typ === 'checkbox') v = v > 0 ? 1 : 0;
    z.felder[id] = v;
  }
  for (const [id, f] of R.felder) {
    if (!f.maxOf) continue;
    const basis = z.felder[f.maxOf];
    if (z.felder[id] > basis) {
      z.felder[id] = basis;
      hinweise.push({
        code: 'angepasst', feld: id,
        params: { feld: f.label, wert: formatZahl(basis), bezug: bezugText(R.felder.get(f.maxOf)) },
      });
    }
  }
  return { zustand: z, hinweise };
}

/* ------------------------------------------------------------------------------------------------
 * Eingaben und Mengen
 * --------------------------------------------------------------------------------------------- */

/**
 * Prüft eine Feldeingabe (Tabelle 03 Abschnitt 4.1): parseMenge, dann max, dann maxOf.
 *
 * Felder (FeldId, Vertrag 4.2): Codes ausschließlich 'komma', 'dezimal', 'minus', 'zeichen', 'max', 'maxOf'.
 * Das sichert der Test T22 über alle Felder der Regeldatei ab.
 *
 * Einstiegsfragen (ap, server, m365, standorte, homeoffice, gb; 03 Abschnitt 2.2): Grenzen min und max der
 * Regeldatei. Liegt ein Wert unter min (ap und standorte haben min 1), gibt es im Vertrag 4.2 keinen
 * passenden Code; still korrigieren verbietet 03 Abschnitt 4.1 ("Nichts wird still korrigiert"). Deshalb
 * liefert die Funktion dort den Code 'min' mit params.min und params.max (beide formatiert), damit ein Text
 * "meldung.min" mit {min} und {max} arbeiten kann. Die Textdatei (4.6.2) enthält diesen Schlüssel; der
 * Rückfall auf "feld.grenzen" in nrn-konfigurator.js greift nur noch, falls er fehlt.
 * @param {string} eingabe
 * @param {FeldId|string} feldId
 * @param {Zustand} zustand
 * @param {Regeln|Object} regeln
 * @returns {{ ok: true, value: number }
 *   | { ok: false, code: 'komma'|'dezimal'|'minus'|'zeichen'|'max'|'maxOf', params: { max?: string, bezug?: string, wert?: string } }
 *   | { ok: false, code: 'min', params: { min: string, max: string } }}  'min' nur bei Einstiegsfragen
 */
export function pruefeMenge(eingabe, feldId, zustand, regeln) {
  const R = idx(regeln);
  const feld = R.felder.get(feldId);
  const frage = feld ? null : R.einstiegNachId.get(feldId);
  if (!feld && !frage) throw new Error(`Feld unbekannt: ${String(feldId)}`);
  const r = parseMenge(eingabe);
  if (!r.ok) return { ok: false, code: r.code, params: {} };
  const v = r.value;
  if (frage) {
    if (v > frage.max) return { ok: false, code: 'max', params: { max: formatZahl(frage.max) } };
    if (v < frage.min) return { ok: false, code: 'min', params: { min: formatZahl(frage.min), max: formatZahl(frage.max) } };
    return { ok: true, value: v };
  }
  if (v > feld.max) return { ok: false, code: 'max', params: { max: formatZahl(feld.max) } };
  if (feld.maxOf) {
    const basis = Number(zustand?.felder?.[feld.maxOf]) || 0;
    if (v > basis) {
      return { ok: false, code: 'maxOf', params: { bezug: bezugText(R.felder.get(feld.maxOf)), wert: formatZahl(basis) } };
    }
  }
  return { ok: true, value: v };
}

/**
 * Warenkorbmenge q einer Position aus der Eingabe n (Spezifikation 03 Abschnitt 1.2).
 * @param {Object} position  Position der Regeldatei (rule, follows, minimumQuantity)
 * @param {number} n  Eingabe (Checkbox 0 oder 1)
 * @param {Record<PositionKey, number>} mengenNachPosition  Eingaben je Position (für flag_follows)
 * @returns {number}
 */
export function warenkorbMenge(position, n, mengenNachPosition) {
  const x = istGanzzahl(n) && n > 0 ? n : 0;
  switch (position.rule) {
    case 'per_unit': return x;
    case 'per_unit_min': return x > 0 ? Math.max(x, position.minimumQuantity) : 0;
    case 'flag': return x > 0 ? 1 : 0;
    case 'flag_follows': {
      const basis = Number(mengenNachPosition?.[position.follows]);
      return x > 0 && istGanzzahl(basis) && basis > 0 ? basis : 0;
    }
    case 'per_started_1000': return x > 0 ? Math.ceil(x / 1000) : 0;
    case 'per_started_1000_after_100': return x <= 100 ? 0 : Math.ceil((x - 100) / 1000);
    case 'per_started_10_or_per_gb': return x > 0 ? Math.ceil(x / 10) : 0;
    default: throw new Error(`Regel unbekannt: ${String(position.rule)}`);
  }
}

/**
 * Eingabe des Rechenkerns aus dem Zustand. Logische Felder (Microsoft 365, Cloud, Lokal) werden auf
 * alle zugehörigen Positionen verteilt; kalkuliere wählt per Schalter die passende.
 * @param {Zustand} zustand
 * @param {Regeln|Object} regeln
 * @returns {{ produkte: ProduktId[], mengen: Record<PositionKey, number>, backup: { storage: string, billingModel: string }, c2c: { model: string }, c2cJeGb: false, mindestVkAusEigenemPreis: true }}
 */
export function mengenFuerRechenkern(zustand, regeln) {
  const R = idx(regeln);
  const mengen = {};
  for (const pos of R.positionen.values()) {
    const v = zustand.felder?.[pos.feld];
    mengen[pos.key] = istGanzzahl(v) && v > 0 ? v : 0;
  }
  return {
    produkte: R.produktReihenfolge.filter(id => zustand.produkte?.[id] === true),
    mengen,
    backup: { storage: zustand.schalter[S_SPEICHER], billingModel: zustand.schalter[S_MODELL] },
    c2c: { model: zustand.schalter[S_C2C] },
    c2cJeGb: false,
    mindestVkAusEigenemPreis: true,
  };
}

/** Status einer Position; fehlt er im KatalogStatus, gilt die Position als gesperrt. */
function statusVon(katalogStatus, key) {
  const s = katalogStatus?.positionen?.[key];
  return s && typeof s === 'object' ? s : { status: 'gesperrt', grund: 'fehlt', variantId: null, preisCent: null, sellingPlanId: null, planName: null };
}

/** Mengen, mit denen Rechenkern und Warenkorb rechnen: Infoposition und gesperrte Positionen 0. */
function wirksameMengen(zustand, R, katalogStatus) {
  const basis = mengenFuerRechenkern(zustand, R);
  for (const pos of R.positionen.values()) {
    if (statusVon(katalogStatus, pos.key).status !== 'ok') basis.mengen[pos.key] = 0;
  }
  return basis;
}

/** Positionen für den Rechenkern: Regeln plus Katalogpreis (VK in Euro), keine weiteren Preisfelder. */
function kernPositionen(R, katalogStatus) {
  const liste = [];
  for (const pos of R.positionen.values()) {
    const s = statusVon(katalogStatus, pos.key);
    const preisCent = s.status === 'ok' && istGanzzahl(s.preisCent) ? s.preisCent : 0;
    const p = {
      product: pos.product, id: pos.id, label: pos.label, rule: pos.rule,
      follows: pos.follows ?? undefined, maxOf: pos.maxOf ?? undefined,
      billingModel: pos.billingModel ?? undefined, storage: pos.storage ?? undefined, c2cModel: pos.c2cModel ?? undefined,
      customerPrice: preisCent / 100,
    };
    if (pos.rule === 'per_unit_min') {
      p.minimumPurchase = { quantity: pos.minimumQuantity, customerPrice: (pos.minimumQuantity * preisCent) / 100 };
    }
    liste.push(p);
  }
  return liste;
}

/** Grundlagentext einer Warenkorbzeile (Eigenschaft "Grundlage"), nur bei Rundung oder Ableitung. */
function grundlageText(pos, n, q, texte) {
  const params = { n: formatZahl(n), q: formatZahl(q), min: formatZahl(pos.minimumQuantity ?? 0) };
  const eins = q === 1 ? '.eins' : '';
  switch (pos.rule) {
    case 'per_started_1000': return text(texte, `grundlage.per_started_1000${eins}`, params);
    case 'per_unit_min': return n < pos.minimumQuantity ? text(texte, 'grundlage.per_unit_min', params) : null;
    case 'per_started_1000_after_100':
    case 'per_started_10_or_per_gb': return text(texte, `grundlage.${pos.rule}${eins}`, params);
    case 'flag_follows': return text(texte, `grundlage.flag_follows.${pos.key}${eins}`, params);
    default: return null;
  }
}

/**
 * Live Abrechnungszeile an einer Position mit Rundungsregel (Spezifikation 03 Abschnitt 2.4),
 * zum Beispiel "1.500 GB ergeben 2 angefangene TB à 39,00 €". Null bei Regeln ohne Rundung,
 * bei Eingabe 0 oder ohne Preis. Mindestabnahme nur unter der Mindestmenge.
 * @param {Object|PositionKey} position
 * @param {number} n  Eingabe
 * @param {number|null} preisCent  Variantenpreis
 * @param {Object} texte
 * @param {Regeln|Object} [regeln]  nötig, wenn position ein Schlüssel ist
 * @returns {string|null}
 */
export function abrechnungszeile(position, n, preisCent, texte, regeln) {
  const pos = typeof position === 'string' ? idx(regeln).positionen.get(position) : position;
  if (!pos) throw new Error(`Position unbekannt: ${String(position)}`);
  if (!istGanzzahl(n) || n <= 0 || !istGanzzahl(preisCent)) return null;
  const q = warenkorbMenge(pos, n, {});
  const params = { n: formatZahl(n), q: formatZahl(q), preis: formatCent(preisCent) };
  const eins = q === 1 ? '.eins' : '';
  switch (pos.rule) {
    case 'per_started_1000':
    case 'per_started_10_or_per_gb':
      // Eingabe 1 braucht das Verb in der Einzahl ("1 GB ergibt"), q ist dann ebenfalls 1 (Befund 41)
      return text(texte, `abrechnungszeile.${pos.rule}${n === 1 ? '.nEins' : eins}`, params);
    case 'per_unit_min':
      if (n >= pos.minimumQuantity) return null;
      return text(texte, 'abrechnungszeile.per_unit_min',
        { ...params, min: formatZahl(pos.minimumQuantity), summe: formatCent(pos.minimumQuantity * preisCent) });
    case 'per_started_1000_after_100':
      if (q === 0) return null;
      return text(texte, `abrechnungszeile.per_started_1000_after_100${eins}`, params);
    default: return null;
  }
}

/**
 * Warenkorbzeilen in Reihenfolge der Regeldatei. Nur wirksame Positionen mit Status ok und q > 0.
 * @param {Zustand} zustand
 * @param {Regeln|Object} regeln
 * @param {KatalogStatus} katalogStatus
 * @param {Object} texte
 * @returns {Zeile[]}
 */
export function warenkorbZeilen(zustand, regeln, katalogStatus, texte) {
  const R = idx(regeln);
  const { mengen } = wirksameMengen(zustand, R, katalogStatus);
  return zeilenAus(zustand, R, katalogStatus, texte, mengen);
}

function zeilenAus(zustand, R, katalogStatus, texte, mengen) {
  const zeilen = [];
  for (const pos of R.positionen.values()) {
    if (pos.warenkorb !== 'zeile' || !aktiv(pos, zustand)) continue;
    const s = statusVon(katalogStatus, pos.key);
    if (s.status !== 'ok') continue;
    const n = mengen[pos.key];
    const q = warenkorbMenge(pos, n, mengen);
    if (q <= 0) continue;
    zeilen.push({
      key: pos.key, produkt: pos.product, label: pos.label, variantId: s.variantId, quantity: q,
      sellingPlanId: s.sellingPlanId ?? null, planName: s.planName ?? null,
      preisCent: s.preisCent, zeileCent: q * s.preisCent, eingabe: n, regel: pos.rule,
      grundlage: grundlageText(pos, n, q, texte),
    });
  }
  return zeilen;
}

/* ------------------------------------------------------------------------------------------------
 * Berechnung
 * --------------------------------------------------------------------------------------------- */

/**
 * Umsatzsteuer in Cent. Modus zeile: Summe der je Zeile gerundeten Steuer; Modus summe: Steuer auf
 * die Nettosumme gerundet. Kaufmännisch gerundet (halbe Cent aufwärts).
 * @param {Zeile[]} zeilen
 * @param {number} satz  Prozent, zum Beispiel 19
 * @param {'zeile'|'summe'} modus
 * @returns {number}
 */
export function ust(zeilen, satz, modus) {
  const liste = Array.isArray(zeilen) ? zeilen : [];
  if (modus === 'summe') {
    const netto = liste.reduce((a, z) => a + z.zeileCent, 0);
    return Math.round((netto * satz) / 100);
  }
  return liste.reduce((a, z) => a + Math.round((z.zeileCent * satz) / 100), 0);
}

/**
 * Komplette Berechnung inklusive Laufzeitinvariante, Sperrgründen und Abrechnungsart (Masterplan 4.2).
 * Sperrgründe in dieser Reihenfolge: nurAngebot, eingabe, leer, gesperrt, abrechnung, invariante.
 * Ungültige Werte im Zustand (keine ganze Zahl, über max, Advanced über Basis) ergeben gueltig false
 * und Sperrgrund eingabe; gerechnet wird dann mit dem normalisierten Zustand.
 * @param {Zustand} zustand
 * @param {Regeln|Object} regeln
 * @param {KatalogStatus} katalogStatus
 * @param {Einstellungen} einstellungen
 * @param {Object} texte
 * @param {{ eingabeFehler?: boolean }} [optionen]  eingabeFehler: die Oberfläche hat ungültige, noch
 *   nicht übernommene Eingaben (Sperrgrund eingabe)
 * @returns {Berechnung}
 */
export function berechne(zustand, regeln, katalogStatus, einstellungen, texte, optionen) {
  const R = idx(regeln);
  const e = normalisiereEinstellungen(einstellungen);
  const fehlerhaft = ungueltigeFelder(zustand, R);
  const gueltig = fehlerhaft.length === 0 && !optionen?.eingabeFehler;
  const z = fehlerhaft.length ? normalisiereZustand(zustand, R) : zustand;

  const eingabe = wirksameMengen(z, R, katalogStatus);
  const zeilen = zeilenAus(z, R, katalogStatus, texte, eingabe.mengen);
  const kern = kalkuliere(kernPositionen(R, katalogStatus), eingabe);

  const nettoCent = zeilen.reduce((a, x) => a + x.zeileCent, 0);
  const kernCent = Math.round(kern.gesamt.vk * 100);
  const invarianteOk = nettoCent === kernCent;
  if (!invarianteOk && typeof console !== 'undefined') {
    console.error('nrn konfigurator: Invariante verletzt', { nettoCent, kernCent });
  }

  const proProdukt = {};
  for (const id of R.produktReihenfolge) proProdukt[id] = 0;
  for (const x of zeilen) proProdukt[x.produkt] += x.zeileCent;

  const ustCent = ust(zeilen, R.ustSatz, e.ustModus);
  const ap = z.einstieg && istGanzzahl(z.einstieg.ap) && z.einstieg.ap > 0
    ? z.einstieg.ap
    : (z.produkte.managedRmm && z.felder['managedRmm.workstations'] > 0 ? z.felder['managedRmm.workstations'] : null);

  const infoZeilen = [];
  const blockiert = [];
  for (const pos of R.positionen.values()) {
    if (!aktiv(pos, z)) continue;
    const n = z.felder[pos.feld];
    if (!(n > 0)) continue;
    const s = statusVon(katalogStatus, pos.key);
    if (s.status === 'info') {
      infoZeilen.push({ key: pos.key, text: text(texte, `infozeile.${pos.key}`, { n: formatZahl(n) }) });
    } else if (s.status === 'gesperrt') {
      blockiert.push({ key: pos.key, grund: s.grund ?? 'fehlt', eingabe: n });
    } else if (pos.rule === 'flag_follows') {
      // Ist die Bezugsposition gesperrt, wird ihre Menge 0 und die abgeleitete Position verschwände still aus
      // Anfrage und Druck (03 Abschnitt 5.4 "Nichts geht still verloren", Kandidat 149): Grund und Menge der Bezugsposition.
      const basisPos = R.positionen.get(pos.follows);
      const nb = basisPos ? z.felder[basisPos.feld] : 0;
      const sb = statusVon(katalogStatus, pos.follows);
      if (basisPos && aktiv(basisPos, z) && nb > 0 && sb.status === 'gesperrt') {
        blockiert.push({ key: pos.key, grund: sb.grund ?? 'fehlt', eingabe: nb });
      }
    }
  }

  // Abrechnung (E3, entscheidungen.md E4)
  const planNamen = [...new Set(zeilen.filter(x => x.sellingPlanId !== null && x.planName).map(x => x.planName))];
  const ohnePlan = zeilen.length === 0 || zeilen.some(x => x.sellingPlanId === null);
  let art;
  if (e.abrechnung === 'angebot') art = 'gesperrt';
  else if (e.abrechnung === 'abo') art = 'abo';
  else if (e.abrechnung === 'auto' && !ohnePlan) art = 'abo';
  else art = e.hinweisAbrechnung ? 'erstmonat' : 'gesperrt';

  let sperrgrund = null;
  if (katalogStatus?.modus === 'nurAngebot' || e.abrechnung === 'angebot') sperrgrund = 'nurAngebot';
  else if (!gueltig) sperrgrund = 'eingabe';
  else if (zeilen.length === 0 && blockiert.length === 0) sperrgrund = 'leer';
  else if (blockiert.length > 0) sperrgrund = 'gesperrt';
  else if (art === 'gesperrt') sperrgrund = 'abrechnung';
  else if (!invarianteOk) sperrgrund = 'invariante';

  return {
    nettoCent,
    ustCent,
    bruttoCent: nettoCent + ustCent,
    jahrCent: 12 * nettoCent,
    jeApCent: ap ? Math.round(nettoCent / ap) : null,
    proProdukt,
    zeilen,
    infoZeilen,
    blockiert,
    abrechnung: { art, planNamen },
    gueltig,
    invarianteOk,
    warenkorbErlaubt: sperrgrund === null,
    sperrgrund,
  };
}

/**
 * Preise der drei Paketkarten aus den Einstiegsantworten (live mit Shopify Preisen). hinweise sind die Hinweise
 * der Empfehlung (zum Beispiel firewallGross), damit die Karte sie schon vor dem Übernehmen zeigt (Kandidat 79).
 * Enthält ein Paket eine gesperrte Position (Variante fehlt, nicht verfügbar, Preis 0, Verkaufsplan fehlt), ist sein
 * Preis unbekannt: nettoCent und jeApCent sind dann null, blockiert nennt die Positionen wie in berechne. Vorher
 * zeigte die Karte nur die Summe der freien Zeilen, nach dem Übernehmen war der Warenkorb gesperrt und die Summe
 * sprang (Prüfung 16.10.2026, Befund 6).
 * @param {Antworten} antworten
 * @param {Regeln|Object} regeln
 * @param {KatalogStatus} katalogStatus
 * @param {Einstellungen} einstellungen
 * @param {Object} texte
 * @returns {Record<PaketId, { nettoCent: number|null, jeApCent: number|null, zeilen: Zeile[], hinweise: Hinweis[],
 *   blockiert: Array<{ key: PositionKey, grund: string, eingabe: number }> }>}
 */
export function paketPreise(antworten, regeln, katalogStatus, einstellungen, texte) {
  const R = idx(regeln);
  const ergebnis = {};
  for (const paket of PAKET_IDS) {
    const { zustand, hinweise } = empfehle(antworten, paket, R);
    const b = berechne(zustand, R, katalogStatus, einstellungen, texte);
    const bekannt = b.blockiert.length === 0;
    ergebnis[paket] = {
      nettoCent: bekannt ? b.nettoCent : null,
      jeApCent: bekannt ? b.jeApCent : null,
      zeilen: b.zeilen,
      hinweise,
      blockiert: b.blockiert,
    };
  }
  return ergebnis;
}

/**
 * Vergleich des Backup Abrechnungsmodells: Nettosumme der ganzen Konfiguration im jeweils anderen
 * Modell (Spezifikation 03 Abschnitt 2.4). Null, wenn Managed Backup nicht einbezogen ist.
 * @param {Zustand} zustand
 * @param {Regeln|Object} regeln
 * @param {KatalogStatus} katalogStatus
 * @param {Einstellungen} einstellungen
 * @param {Object} texte
 * @returns {{ modell: 'workload'|'gigabyte', nettoCent: number } | null}
 */
export function backupVergleich(zustand, regeln, katalogStatus, einstellungen, texte) {
  const R = idx(regeln);
  if (!zustand?.produkte?.managedBackup) return null;
  const modell = zustand.schalter[S_MODELL] === 'gigabyte' ? 'workload' : 'gigabyte';
  const anders = { ...zustand, schalter: { ...zustand.schalter, [S_MODELL]: modell } };
  return { modell, nettoCent: berechne(anders, R, katalogStatus, einstellungen, texte).nettoCent };
}

/* ------------------------------------------------------------------------------------------------
 * Link (Hash) und Konfigurationsnummer (03 Abschnitt 6.4 und 6.5, Masterplan 4.8)
 * --------------------------------------------------------------------------------------------- */

const UTF8 = new TextEncoder();

/**
 * FNV 1a 32 Bit über die UTF 8 Bytes, 8 Hexziffern klein.
 * @param {string} s
 * @returns {string}
 */
export function fnv1a32(s) {
  let h = 0x811c9dc5;
  for (const b of UTF8.encode(s)) {
    h ^= b;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

const FNV64_START = 0xcbf29ce484222325n;
const FNV64_PRIM = 0x100000001b3n;
const MASKE64 = (1n << 64n) - 1n;

function fnv1a64(s) {
  let h = FNV64_START;
  for (const b of UTF8.encode(s)) {
    h ^= BigInt(b);
    h = (h * FNV64_PRIM) & MASKE64;
  }
  return h;
}

function schalterCode(R, id, wert) {
  return R.schalterNachId.get(id).werte.find(w => w.id === wert)?.code
    ?? R.schalterNachId.get(id).werte.find(w => w.id === R.schalterNachId.get(id).standard).code;
}

/** Teile des Hash aus einem gültigen Zustand (ohne Präfixe). */
function hashTeile(z, R) {
  const e = z.einstieg ? EINSTIEG_IDS.map(id => z.einstieg[id]).join('.') : '';
  const pk = z.paket ? PAKET_CODES[z.paket] : '';
  const p = R.produktReihenfolge.filter(id => z.produkte[id]).map(id => R.produkte.get(id).code).join('');
  const o = schalterCode(R, S_SPEICHER, z.schalter[S_SPEICHER])
    + schalterCode(R, S_MODELL, z.schalter[S_MODELL])
    + schalterCode(R, S_C2C, z.schalter[S_C2C]);
  const m = [];
  for (const [id, f] of R.felder) {
    const v = z.felder[id];
    if (v > 0 && z.produkte[f.product] && sichtbar(f, z)) m.push(`${f.code}.${v}`);
  }
  return { e, pk, p, o, m: m.join(',') };
}

/**
 * Kanonischer Hash ohne "#" (Grammatik 03 Abschnitt 6.4): v=1;e=…;pk=…;p=…;o=…;m=…;c=…
 * Leere Teile e, pk, p, m entfallen; m enthält nur Werte > 0 aktiver Produkte, C2C nur das aktive Modell.
 * @param {Zustand} zustand
 * @param {Regeln|Object} regeln
 * @returns {string}
 */
export function serialisiere(zustand, regeln) {
  const R = idx(regeln);
  const t = hashTeile(normalisiereZustand(zustand, R), R);
  const teile = ['v=1'];
  if (t.e) teile.push(`e=${t.e}`);
  if (t.pk) teile.push(`pk=${t.pk}`);
  if (t.p) teile.push(`p=${t.p}`);
  teile.push(`o=${t.o}`);
  if (t.m) teile.push(`m=${t.m}`);
  const kern = teile.join(';');
  return `${kern};c=${fnv1a32(kern)}`;
}

function ungueltigerLink() {
  return { zustand: null, kurzform: null, hinweise: [{ code: 'linkUngueltig' }] };
}

/** Teil eines Konfigurator Links: ein Teil aus serialisiere oder eine Kurzform, roh oder mit kodiertem "=", am Anfang oder nach einem Semikolon. */
const KONFIG_LINK = /(?:^|;)\s*(?:v|e|pk|p|o|m|c|paket|modul)(?:=|%3D)/i;

/**
 * Sieht der Hash nach einem Konfigurator Link aus (v=1;…, paket=…, modul=…)? Andere Sprungziele der Seite wie
 * #MainContent (Link "Zum Inhalt springen") sind kein Link des Konfigurators und dürfen weder den Hinweis
 * linkUngueltig auslösen noch die Wiederherstellung der letzten Konfiguration verhindern (Prüfung 16.10.2026,
 * Befund 4). Großschreibung zählt als Konfigurator Link, damit ein verstümmelter Link weiter den Hinweis bekommt.
 * @param {string} hash  mit oder ohne "#"
 * @returns {boolean}
 */
export function istKonfigLink(hash) {
  let s = typeof hash === 'string' ? hash : '';
  if (s.startsWith('#')) s = s.slice(1);
  if (s.includes('%')) {
    try { s = decodeURIComponent(s); } catch { /* roh prüfen */ }
  }
  return KONFIG_LINK.test(s.trim());
}

/** Kürzt und bereinigt unbekannte Angaben für den Hinweistext (nur Buchstaben, Ziffern, Punkt, Gleich). */
function anzeigeToken(t) {
  return String(t).replace(/[^A-Za-z0-9.=_]/g, '').slice(0, 24);
}

/**
 * Liest einen Hash (mit oder ohne "#"). Wirft nie. Kurzformen #paket=b|w|e und #modul=<code> ergeben
 * zustand null und kurzform. Hinweise nach 03 Abschnitt 6.4 (Codes ohne Präfix "hinweise.").
 * @param {string} hash
 * @param {Regeln|Object} regeln
 * @returns {{ zustand: Zustand|null, kurzform: { paket: PaketId|null, modul: ProduktId|null }|null, hinweise: Hinweis[] }}
 */
export function deserialisiere(hash, regeln) {
  const R = idx(regeln);
  try {
    return lies(hash, R);
  } catch {
    return ungueltigerLink();
  }
}

function lies(hash, R) {
  let s = typeof hash === 'string' ? hash : (hash === null || hash === undefined ? '' : String(hash));
  if (s.length > MAX_HASH_LAENGE + 1) return ungueltigerLink();
  if (s.startsWith('#')) s = s.slice(1);
  if (s.includes('%')) {
    try { s = decodeURIComponent(s); } catch { /* roh weiterlesen */ }
  }
  s = s.trim();
  if (s === '') return { zustand: null, kurzform: null, hinweise: [] };
  if (s.length > MAX_HASH_LAENGE) return ungueltigerLink();

  // Kurzformen
  if (/^(paket|modul)=/.test(s)) {
    const pk = /^paket=([a-z])$/.exec(s);
    if (pk && pk[1] !== 'x' && PAKET_NACH_CODE[pk[1]]) {
      return { zustand: null, kurzform: { paket: PAKET_NACH_CODE[pk[1]], modul: null }, hinweise: [] };
    }
    const mo = /^modul=([a-z])$/.exec(s);
    if (mo && R.produktNachCode.has(mo[1])) {
      return { zustand: null, kurzform: { paket: null, modul: R.produktNachCode.get(mo[1]) }, hinweise: [] };
    }
    return ungueltigerLink();
  }

  // Teile zerlegen, bei doppeltem Namen zählt der erste
  const teile = new Map();
  const unbekannt = [];
  let doppelt = false;
  for (const roh of s.split(';')) {
    if (roh === '') continue;
    const i = roh.indexOf('=');
    if (i < 1) { unbekannt.push(roh); continue; }
    const name = roh.slice(0, i);
    const wert = roh.slice(i + 1);
    if (!['v', 'e', 'pk', 'p', 'o', 'm', 'c'].includes(name)) { unbekannt.push(name); continue; }
    if (teile.has(name)) { doppelt = true; continue; }
    teile.set(name, wert);
  }
  if (teile.get('v') !== '1') return ungueltigerLink();

  const hinweise = [];
  const z = standardZustand(R);

  // Prüfsumme über alles vor ";c="
  let pruefsummeOk = false;
  const cPos = s.lastIndexOf(';c=');
  if (teile.has('c') && cPos > 0) pruefsummeOk = teile.get('c') === fnv1a32(s.slice(0, cPos));

  // Einstieg
  if (teile.has('e')) {
    const werte = teile.get('e').split('.');
    let ok = werte.length === EINSTIEG_IDS.length;
    const einstieg = {};
    if (ok) {
      EINSTIEG_IDS.forEach((id, k) => {
        const f = R.einstiegNachId.get(id);
        if (!ZAHL_IM_LINK.test(werte[k])) { ok = false; return; }
        const v = Number(werte[k]);
        if (v < f.min || v > f.max) ok = false;
        einstieg[id] = v;
      });
    }
    if (ok) z.einstieg = einstieg;
    else hinweise.push({ code: 'einstiegUngueltig' });
  }

  // Paket
  if (teile.has('pk')) {
    const paket = PAKET_NACH_CODE[teile.get('pk')];
    if (paket && teile.get('pk').length === 1) z.paket = paket;
    else unbekannt.push(`pk=${teile.get('pk')}`);
  }

  // Produkte
  if (teile.has('p')) {
    const gesehen = new Set();
    for (const ch of teile.get('p')) {
      const id = R.produktNachCode.get(ch);
      if (!id) { unbekannt.push(`p=${ch}`); continue; }
      if (gesehen.has(ch)) { doppelt = true; continue; }
      gesehen.add(ch);
      z.produkte[id] = true;
    }
  }

  // Optionen: Speicherort, Modell, C2C
  if (teile.has('o')) {
    const o = teile.get('o');
    let fehlerhaft = o.length !== 3;
    [S_SPEICHER, S_MODELL, S_C2C].forEach((id, k) => {
      const w = R.schalterNachId.get(id).werte.find(x => x.code === o[k]);
      if (w) z.schalter[id] = w.id;
      else fehlerhaft = true;
    });
    if (fehlerhaft) unbekannt.push(`o=${o}`);
  }

  // Mengen
  if (teile.has('m')) {
    const gesehen = new Set();
    const ungueltig = new Set();
    for (const eintrag of teile.get('m').split(',')) {
      if (eintrag === '') continue;
      const j = eintrag.indexOf('.');
      if (j < 1) { unbekannt.push(eintrag); continue; }
      const code = eintrag.slice(0, j);
      const feldId = R.feldNachCode.get(code);
      if (!feldId) { unbekannt.push(code); continue; }
      if (gesehen.has(code)) { doppelt = true; continue; }
      gesehen.add(code);
      const f = R.felder.get(feldId);
      const zahl = eintrag.slice(j + 1);
      const v = ZAHL_IM_LINK.test(zahl) ? Number(zahl) : NaN;
      if (!Number.isInteger(v) || v > f.max) {
        z.felder[feldId] = 0;
        if (!ungueltig.has(feldId)) {
          ungueltig.add(feldId);
          hinweise.push({ code: 'wertUngueltig', feld: feldId, params: { feld: f.label } });
        }
        continue;
      }
      z.felder[feldId] = v;
    }
  }

  // C2C Werte des inaktiven Modells verwerfen
  for (const [id, f] of R.felder) if (!sichtbar(f, z)) z.felder[id] = 0;

  const angepasst = passeAbhaengigkeitenAn(z, R);

  if (unbekannt.length) {
    const liste = [...new Set(unbekannt.map(anzeigeToken).filter(Boolean))].slice(0, 10);
    hinweise.unshift({ code: 'unbekannt', params: { liste: liste.length ? liste.join(', ') : '?' } });
  }
  if (doppelt) hinweise.push({ code: 'doppelt' });
  hinweise.push(...angepasst.hinweise);
  if (!pruefsummeOk) hinweise.push({ code: 'linkUnvollstaendig' });

  return { zustand: angepasst.zustand, kurzform: null, hinweise };
}

/** Warenkorbzeilen ohne Katalog (Position, Eingabe, Menge), nur zum Vergleich innerhalb der Nummer. */
function zeilenSignatur(z, R) {
  const { mengen } = mengenFuerRechenkern(z, R);
  const teile = [];
  for (const pos of R.positionen.values()) {
    if (pos.warenkorb !== 'zeile' || !aktiv(pos, z)) continue;
    const q = warenkorbMenge(pos, mengen[pos.key], mengen);
    if (q > 0) teile.push(`${pos.key}.${mengen[pos.key]}.${q}`);
  }
  return teile.join(',');
}

/**
 * Konfigurationsnummer: FNV 1a 64 Bit über den Kern "p=…;o=…;m=…", obere 40 Bit als 8 Zeichen
 * Crockford Base32 (Masterplan 4.8). Nur für die Nummer (Anzeige und Link behalten den gewählten Wert)
 * wird ein Schalter auf den Standard gesetzt, wenn das die Warenkorbzeilen nicht ändert; Werte des
 * inaktiven C2C Modells zählen dabei nicht, wie beim Lesen eines Links. Das umfasst Backup oder C2C nicht
 * einbezogen, Speicherort im Gigabyte Modell sowie Backup bzw. C2C ohne Werte, die vom Schalter abhängen.
 * Zustände, die sich nur in solchen Schaltern unterscheiden, tragen so dieselbe Nummer; verschiedene
 * Warenkorbzeilen ergeben weiterhin verschiedene.
 * @param {Zustand} zustand
 * @param {Regeln|Object} regeln
 * @returns {string}  zum Beispiel "XAKK9VP4"
 */
export function konfigNummer(zustand, regeln) {
  const R = idx(regeln);
  const n = normalisiereZustand(zustand, R);
  const felder = { ...n.felder };
  for (const [id, f] of R.felder) if (!sichtbar(f, n)) felder[id] = 0;
  let z = { ...n, felder };
  const zeilen = zeilenSignatur(z, R);
  for (const id of SCHALTER_IDS) {
    const standard = R.schalterNachId.get(id).standard;
    if (z.schalter[id] === standard) continue;
    const probe = { ...z, schalter: { ...z.schalter, [id]: standard } };
    if (zeilenSignatur(probe, R) === zeilen) z = probe;
  }
  const t = hashTeile(z, R);
  const kern = `p=${t.p};o=${t.o};m=${t.m}`;
  let wert = fnv1a64(kern) >> 24n;
  let nr = '';
  for (let k = 0; k < 8; k++) {
    nr = CROCKFORD[Number(wert & 31n)] + nr;
    wert >>= 5n;
  }
  return nr;
}

/**
 * Anzeigeform der Nummer in zwei Vierergruppen ("XAKK 9VP4").
 * @param {string} nr
 * @returns {string}
 */
export function formatNummer(nr) {
  const s = String(nr ?? '').replace(/\s+/g, '');
  return s.length > 4 ? `${s.slice(0, 4)} ${s.slice(4)}` : s;
}

/* ------------------------------------------------------------------------------------------------
 * Warenkorb (03 Abschnitt 5.2 und 5.3)
 * --------------------------------------------------------------------------------------------- */

/**
 * Payload für POST /cart/add.js. Eigenschaften je Zeile (alle Werte Text):
 * sichtbar Konfiguration (immer), Grundlage (wenn gesetzt), Abrechnung (nur erste Zeile und nur, wenn
 * die Konfiguration Zeilen ohne Verkaufsplan hat); verborgen _nrn_cfg, _nrn_pos, _nrn_regel,
 * _nrn_eingabe, _nrn_v, _nrn_link (nur erste Zeile). selling_plan nur bei gesetztem Plan.
 * @param {Zeile[]} zeilen
 * @param {{ nummer: string, hash: string, stand: string, abrechnungKurz: string|null }} meta
 * @returns {{ items: Array<{ id: number, quantity: number, selling_plan?: number, properties: Record<string, string> }> }}
 */
export function warenkorbPayload(zeilen, meta) {
  const nummer = String(meta?.nummer ?? '').replace(/\s+/g, '');
  const link = String(meta?.hash ?? '').replace(/^#/, '');
  const stand = String(meta?.stand ?? '');
  const kurz = typeof meta?.abrechnungKurz === 'string' ? meta.abrechnungKurz.trim() : '';
  const liste = Array.isArray(zeilen) ? zeilen : [];
  const ohnePlan = liste.some(z => z.sellingPlanId === null || z.sellingPlanId === undefined);
  const items = liste.map((z, i) => {
    const properties = { Konfiguration: formatNummer(nummer) };
    if (z.grundlage) properties.Grundlage = String(z.grundlage);
    if (i === 0 && ohnePlan && kurz) properties.Abrechnung = kurz;
    properties._nrn_cfg = nummer;
    properties._nrn_pos = String(z.key);
    properties._nrn_regel = String(z.regel);
    properties._nrn_eingabe = String(z.eingabe);
    properties._nrn_v = `1/${stand}`;
    if (i === 0) properties._nrn_link = link;
    const item = { id: z.variantId, quantity: z.quantity };
    if (z.sellingPlanId !== null && z.sellingPlanId !== undefined) item.selling_plan = z.sellingPlanId;
    item.properties = properties;
    return item;
  });
  return { items };
}

function zeilenEigenschaft(item, name) {
  const p = item?.properties;
  if (!p || typeof p !== 'object') return null;
  if (Array.isArray(p)) {
    const e = p.find(x => Array.isArray(x) ? x[0] === name : x?.name === name);
    if (!e) return null;
    return Array.isArray(e) ? e[1] : e.value;
  }
  return Object.hasOwn(p, name) ? p[name] : null;
}

function zeilenCent(item) {
  for (const k of ['original_line_price', 'line_price', 'final_line_price']) {
    if (istGanzzahl(item?.[k])) return item[k];
  }
  const preis = Number(item?.price);
  const menge = Number(item?.quantity);
  return Number.isFinite(preis) && Number.isFinite(menge) ? Math.round(preis * menge) : 0;
}

/**
 * Lage im Warenkorb vor dem Hinzufügen: leer (keine Konfigurator Zeilen), gleich (diese Nummer liegt
 * schon im Warenkorb) oder andere. alteNummern, alteKeys, alteZeilenAnzahl und alteSummeCent betreffen
 * nur Konfigurator Zeilen anderer Nummern; fremde Zeilen bleiben unberührt.
 * @param {AjaxCart} cart
 * @param {string} nummer
 * @returns {{ lage: 'leer'|'gleich'|'andere', alteNummern: string[], alteKeys: string[], alteZeilenAnzahl: number, alteSummeCent: number }}
 */
export function planeWarenkorb(cart, nummer) {
  const eigene = String(nummer ?? '').replace(/\s+/g, '');
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const alteNummern = [];
  const alteKeys = [];
  let alteSummeCent = 0;
  let gleich = false;
  let irgendeine = false;
  for (const item of items) {
    const cfg = zeilenEigenschaft(item, '_nrn_cfg');
    if (cfg === null || cfg === undefined || String(cfg).trim() === '') continue;
    irgendeine = true;
    const nr = String(cfg).replace(/\s+/g, '');
    if (nr === eigene) { gleich = true; continue; }
    if (!alteNummern.includes(nr)) alteNummern.push(nr);
    if (item.key !== undefined && item.key !== null) alteKeys.push(String(item.key));
    alteSummeCent += zeilenCent(item);
  }
  const lage = !irgendeine ? 'leer' : (gleich ? 'gleich' : 'andere');
  return { lage, alteNummern, alteKeys, alteZeilenAnzahl: alteKeys.length, alteSummeCent };
}

/**
 * Abgleich nach dem Hinzufügen: Summe original_line_price aller Zeilen dieser Nummer gleich nettoCent.
 * @param {AjaxCart} cart
 * @param {string} nummer
 * @param {number} nettoCent
 * @returns {boolean}
 */
export function pruefeWarenkorbAbgleich(cart, nummer, nettoCent) {
  const eigene = String(nummer ?? '').replace(/\s+/g, '');
  const items = Array.isArray(cart?.items) ? cart.items : [];
  let summe = 0;
  let gefunden = 0;
  for (const item of items) {
    const cfg = zeilenEigenschaft(item, '_nrn_cfg');
    if (cfg === null || String(cfg).replace(/\s+/g, '') !== eigene) continue;
    gefunden += 1;
    summe += zeilenCent(item);
  }
  return gefunden > 0 && summe === nettoCent;
}

/* ------------------------------------------------------------------------------------------------
 * Anfragetext (03 Abschnitt 6.6, Vertrag mit der Kontaktseite)
 * --------------------------------------------------------------------------------------------- */

/**
 * Eingabe eines Zahlfelds mit Einheit, zum Beispiel "11 Lizenzen". Bei genau 1 gilt die Einzahl je Feld aus der
 * Textdatei (feld.eingabe.eins.<feld>, etwa "1 Ziel"), falls sie dort steht, sonst die Einheit der Regeldatei.
 * Sichtpruefung 12.10.2026, Befund abschluss-8 (Druckansicht und Anfrage zeigten "1 Ziele").
 * @param {{ id: FeldId, einheit: string }} feld
 * @param {number} n
 * @param {Object} texte
 * @returns {string}
 */
export function eingabeMitEinheit(feld, n, texte) {
  if (n === 1) {
    const eins = text(texte, `feld.eingabe.eins.${feld.id}`);
    if (!eins.startsWith('⟦')) return eins;
  }
  return `${formatZahl(n)} ${feld.einheit}`;
}

/**
 * Anfragetext für die Kontaktseite. Keine personenbezogenen Daten. Die Arbeitsplatzzahl steht ohne
 * Tausenderpunkt, damit die Kontaktseite sie per Muster "Arbeitsplätze: (\d+)" erkennt.
 * Einrichtung und Laufzeit (Einstellungen einrichtung_text und laufzeit_text, E15 und E14) folgen als
 * eigene Zeilen nach dem Bruttobetrag; ein leerer Wert ergibt keine Zeile.
 * @param {Zustand} zustand
 * @param {Berechnung} berechnung
 * @param {Regeln|Object} regeln
 * @param {Object} texte
 * @param {{ url: string, paketName: string|null, abrechnungHinweis: string|null,
 *   einrichtungText?: string|null, laufzeitText?: string|null }} meta
 * @returns {string}
 */
export function anfrageText(zustand, berechnung, regeln, texte, meta) {
  const R = idx(regeln);
  const z = normalisiereZustand(zustand, R);
  const nummer = formatNummer(konfigNummer(z, R));
  const zeilen = [];
  zeilen.push(text(texte, 'anfrage.kopf', { nummer }));
  if (meta?.url) zeilen.push(text(texte, 'anfrage.link', { url: meta.url }));
  const ap = z.einstieg?.ap > 0 ? z.einstieg.ap
    : (z.produkte.managedRmm && z.felder['managedRmm.workstations'] > 0 ? z.felder['managedRmm.workstations'] : null);
  if (ap) zeilen.push(text(texte, 'anfrage.arbeitsplaetze', { ap: String(ap) }));
  if (meta?.paketName) zeilen.push(text(texte, 'anfrage.paket', { paket: meta.paketName }));

  const b = berechnung ?? {};
  const nachKey = new Map((b.zeilen ?? []).map(x => [x.key, x]));
  const gesperrtNachKey = new Map((b.blockiert ?? []).map(x => [x.key, x]));
  const infoNachKey = new Map((b.infoZeilen ?? []).map(x => [x.key, x]));

  for (const id of R.produktReihenfolge) {
    const block = [];
    for (const pos of R.positionen.values()) {
      if (pos.product !== id) continue;
      const x = nachKey.get(pos.key);
      if (x) {
        const preis = formatCent(x.preisCent);
        const summe = formatCent(x.zeileCent);
        // Grundlage nur bei Rundung (Menge weicht von der Eingabe ab). Bei abgeleiteten Positionen (flag_follows)
        // wiederholt sie nur die Menge und las sich als "folgt 10 Postfächern × 0,30 €" (Befund abschluss-9).
        block.push(`  ${x.grundlage && pos.rule !== 'flag_follows'
          ? text(texte, 'anfrage.zeileGrundlage', { label: x.label, grundlage: x.grundlage, preis, summe })
          : text(texte, 'anfrage.zeile', { label: x.label, menge: formatZahl(x.quantity), preis, summe })}`);
        continue;
      }
      const g = gesperrtNachKey.get(pos.key);
      if (g) {
        const feld = R.felder.get(pos.feld);
        // Checkbox wie in der Druckansicht ohne den Haken als Menge: flag "pauschal", flag_follows mit der Menge der
        // Bezugsposition ("folgt 6 internen Scan Zielen"), auch wenn diese selbst gesperrt ist (Kandidaten 103 und 149)
        let eingabe;
        if (feld.typ === 'zahl') eingabe = eingabeMitEinheit(feld, g.eingabe, texte);
        else if (pos.rule === 'flag_follows') {
          const n = z.felder[pos.feld];
          eingabe = grundlageText(pos, n, warenkorbMenge(pos, n, mengenFuerRechenkern(z, R).mengen), texte);
        } else eingabe = feld.einheit;
        block.push(`  ${text(texte, 'anfrage.zeileGesperrt', { label: pos.label, eingabe })}`);
        continue;
      }
      const info = infoNachKey.get(pos.key);
      if (info) block.push(`  ${info.text}`);
    }
    if (block.length) {
      zeilen.push('');
      zeilen.push(R.produkte.get(id).titel);
      zeilen.push(...block);
    }
  }

  zeilen.push('');
  let netto = text(texte, 'anfrage.netto', { betrag: formatCent(b.nettoCent ?? 0) });
  if ((b.blockiert ?? []).length) netto += ` (${text(texte, 'summe.ohneGesperrte')})`;
  zeilen.push(netto);
  zeilen.push(text(texte, 'anfrage.ust', { satz: formatZahl(R.ustSatz), betrag: formatCent(b.ustCent ?? 0) }));
  zeilen.push(text(texte, 'anfrage.brutto', { betrag: formatCent(b.bruttoCent ?? 0) }));
  const einrichtung = typeof meta?.einrichtungText === 'string' ? meta.einrichtungText.trim() : '';
  if (einrichtung) zeilen.push(text(texte, 'anfrage.einrichtung', { wert: einrichtung }));
  const laufzeit = typeof meta?.laufzeitText === 'string' ? meta.laufzeitText.trim() : '';
  if (laufzeit) zeilen.push(text(texte, 'anfrage.laufzeit', { wert: laufzeit }));
  if (meta?.abrechnungHinweis) {
    zeilen.push('');
    // Der Hinweis zum ersten Monat (Einstellung hinweis_abrechnung) spricht den Kunden an ("Heute zahlen Sie …").
    // In der Nachricht des Kunden steht er darum als bezeichnete Angabe wie der Abo Hinweis "Abrechnung: {plan}",
    // der schon so übergeben wird (Befund abschluss-9).
    const hinweis = String(meta.abrechnungHinweis);
    zeilen.push(b.abrechnung?.art === 'erstmonat' ? text(texte, 'summe.abrechnungPlan', { plan: hinweis }) : hinweis);
  }
  return zeilen.join('\n');
}
